import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import useManagerStore from '../store/managerStore'
import {
  getCreditStatus,
  getSubscriptionPlans,
  getBillingTopupPackages,
  initiateSubscription,
  initiateTopup,
} from '../api/billing'
import { planDurations, planOption, sellablePlans } from '../lib/billingPlans'
import { billingView } from '../lib/billingStatus'

const BAR_COLOR = { ok: 'bg-green-500', warning: 'bg-amber-500', danger: 'bg-red-500' }
const formatVND = (n) =>
  (n == null ? '0' : Number(n).toLocaleString('vi-VN')) + ' ₫'

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('vi-VN') : '—'

function Skeleton({ className }) {
  return <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
}

function Spinner({ className = '' }) {
  return (
    <span
      className={`inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent ${className}`}
      aria-hidden="true"
    />
  )
}

function monthlyEquivalent(option) {
  const months = Number(option?.duration_months) || 1
  return Math.round((Number(option?.price_vnd) || 0) / months)
}

export default function BillingPage() {
  const { t } = useTranslation()
  const { tenantId } = useManagerStore()

  const [status, setStatus]                 = useState(null)
  const [plans, setPlans]                   = useState(null)
  const [topupPackages, setTopupPackages]   = useState(null)
  const [loadError, setLoadError]           = useState(false)
  const [durationMonths, setDurationMonths] = useState(6)
  const [subscribingTier, setSubscribingTier] = useState(null)
  const [topupKey, setTopupKey]             = useState(null)
  const [reloadKey, setReloadKey]           = useState(0)

  useEffect(() => {
    let cancelled = false
    if (!tenantId) return

    async function load() {
      try {
        const [s, planList, tp] = await Promise.all([
          getCreditStatus(tenantId),
          getSubscriptionPlans(),
          getBillingTopupPackages(),
        ])
        if (cancelled) return
        setStatus(s)
        setPlans(planList)
        setTopupPackages(tp)
        setLoadError(false)
      } catch (err) {
        Sentry.captureException(err, {
          tags: { action: 'load_billing' },
          extra: { tenantId },
        })
        if (!cancelled) setLoadError(true)
      }
    }
    load()
    return () => { cancelled = true }
  }, [tenantId, reloadKey])

  useEffect(() => {
    function resetCheckoutState() {
      setSubscribingTier(null)
      setTopupKey(null)
      setReloadKey((key) => key + 1)
    }

    function handlePageShow(event) {
      if (event.persisted) resetCheckoutState()
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') resetCheckoutState()
    }

    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const billing          = billingView(status)
  const isInternal       = !!billing?.isInternal
  const creditsRemaining = billing?.credits.remaining ?? 0
  const creditsTotal     = billing?.credits.total ?? 0
  const pctRemaining     = 100 - (billing?.credits.usedPct ?? 0)
  const daysRemaining    = billing?.timeLeft && billing.timeLeft.ms > 0 ? billing.timeLeft.days : null
  const currentPlanCode  = billing?.planCode ?? null
  const pctColor         = BAR_COLOR[billing?.tone ?? 'ok']

  async function handleSubscribe(planCode, months) {
    if (!tenantId || subscribingTier) return
    setSubscribingTier(planCode)
    try {
      const data = await initiateSubscription(tenantId, planCode, months)
      if (data?.payment_url) {
        try {
          sessionStorage.setItem('onepay_subscription_checkout', JSON.stringify({
            tenantId,
            planCode,
            durationMonths: months,
            txnRef: data.txn_ref ?? null,
            baseline: status ? {
              tier: status.tier ?? null,
              status: status.status ?? null,
              billingCycleStart: status.billing_cycle_start ?? null,
              billingCycleEnd: status.billing_cycle_end ?? null,
              termEnd: status.term_end ?? null,
            } : null,
          }))
        } catch (storageError) {
          Sentry.captureException(storageError, {
            tags: { action: 'store_subscription_checkout' },
          })
        }
        window.location.assign(data.payment_url)
        return
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'initiate_subscription' },
        extra: { tenantId, planCode, durationMonths: months },
      })
    }
    setSubscribingTier(null)
  }

  async function handleTopup(pkgKey) {
    if (!tenantId || topupKey) return
    setTopupKey(pkgKey)
    try {
      const data = await initiateTopup(tenantId, pkgKey)
      if (data?.payment_url) {
        window.location.href = data.payment_url
        return
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'initiate_topup' },
        extra: { tenantId, pkgKey },
      })
    }
    setTopupKey(null)
  }

  if (loadError) {
    return (
      <ResponsiveShell title="Quản lý tín dụng">
        <div className="flex flex-col gap-3 py-3">
          <div className="card p-4 text-center">
            <p className="text-sm text-text-main">Không thể tải dữ liệu</p>
            <button
              className="btn-primary mt-3 mx-auto"
              onClick={() => {
                setLoadError(false)
                setReloadKey((k) => k + 1)
              }}
            >
              Thử lại
            </button>
          </div>
        </div>
      </ResponsiveShell>
    )
  }

  const saleablePlans = sellablePlans(plans)
  const durationChoices = planDurations(plans)
  // The catalog decides which terms exist; fall back to the first one it offers.
  const selectedMonths = durationChoices.some(({ months }) => months === durationMonths)
    ? durationMonths
    : durationChoices[0]?.months ?? durationMonths

  const topupEntries = topupPackages ? Object.entries(topupPackages) : []
  const textPackages  = topupEntries.filter(([, p]) => (p?.type ?? '').toLowerCase() === 'text')
  const voicePackages = topupEntries.filter(([, p]) => (p?.type ?? '').toLowerCase() === 'voice')

  return (
    <ResponsiveShell title="Quản lý tín dụng">
      <div className="flex flex-col gap-4 py-3">

        {/* SECTION 1 — Credit Status */}
        <div className="card p-4">
          <div className="text-text-hint text-xs uppercase tracking-wider">Tình trạng tín dụng</div>

          {status === null ? (
            <div className="mt-3 space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-10 w-56" />
              <Skeleton className="h-2 w-full mt-3" />
              <Skeleton className="h-3 w-32 mt-2" />
            </div>
          ) : (
            <>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {currentPlanCode && (
                  <span className="pill bg-primary-soft text-primary">
                    {status?.tier_name ?? currentPlanCode}
                  </span>
                )}
                {status?.status && (
                  <span className="pill bg-zinc-100 text-text-muted">
                    {status.status}
                  </span>
                )}
                {isInternal && (
                  <span className="pill bg-emerald-500/10 text-emerald-600">
                    Nội bộ — không giới hạn
                  </span>
                )}
              </div>

              <div className="flex items-baseline mt-3">
                <span className="text-4xl font-black text-primary">{creditsRemaining}</span>
                <span className="text-text-hint text-sm ml-2">/ {creditsTotal}</span>
              </div>

              <div className="w-full h-2 bg-gray-100 rounded-full mt-3">
                <div
                  className={`h-2 rounded-full ${pctColor}`}
                  style={{ width: `${pctRemaining}%` }}
                />
              </div>

              <div className="flex justify-between mt-2">
                <span className="text-text-hint text-xs">
                  {t('billingStatus.remainingPct', { pct: pctRemaining })}
                </span>
                {daysRemaining !== null && (
                  <span className="text-text-hint text-xs">
                    {t('billingStatus.daysLeft', { count: daysRemaining })}
                  </span>
                )}
              </div>

            </>
          )}
        </div>

        {/* SECTION 2 — Subscription Tiers */}
        {!isInternal && (
          <div>
            <p className="text-primary font-bold text-sm mb-2">Gói đăng ký</p>

            <div className="card p-3 mb-2">
              <div className="text-text-hint text-xs mb-2">Thời hạn</div>
              <div className="flex gap-2">
                {durationChoices.map(({ months, label, discountPercent }) => (
                  <button
                    key={months}
                    onClick={() => setDurationMonths(months)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      selectedMonths === months
                        ? 'bg-primary text-white'
                        : 'bg-page-bg text-text-muted hover:bg-zinc-100'
                      }`}
                  >
                    {label ?? t('billingPlans.durationMonths', { count: months })}
                    {discountPercent ? ` • ${t('billingPlans.savePercent', { percent: discountPercent })}` : null}
                  </button>
                ))}
              </div>
            </div>

            {plans === null ? (
              <div className="grid grid-cols-2 gap-2">
                <Skeleton className="h-44 w-full" />
                <Skeleton className="h-44 w-full" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {saleablePlans.map((plan) => {
                  const isCurrent = currentPlanCode === plan.code
                  const selectedOption = planOption(plan, selectedMonths)
                  const totalPrice = selectedOption?.price_vnd ?? 0
                  const displayMonthlyPrice = monthlyEquivalent(selectedOption)
                  const features = Array.isArray(plan?.features) ? plan.features : []
                  const busy         = subscribingTier === plan.code
                  return (
                    <div
                      key={plan.code}
                      className={`card p-4 ${isCurrent ? 'ring-2 ring-primary' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-primary font-bold text-sm">
                          {plan?.name ?? plan.code}
                        </span>
                        {isCurrent && (
                          <span className="pill bg-primary-soft text-primary">Hiện tại</span>
                        )}
                      </div>
                      <div className="mt-2">
                        <div className="text-2xl font-black text-text-main">
                          {selectedOption ? formatVND(displayMonthlyPrice) : '—'}
                          <span className="text-xs text-text-hint font-normal">/tháng</span>
                        </div>
                        {selectedOption && (
                          <div className="text-xs text-text-hint mt-1">Tổng: {formatVND(totalPrice)}</div>
                        )}
                        {selectedOption ? (
                          <div className="text-xs text-text-hint mt-1">{selectedOption.label}</div>
                        ) : (
                          <div className="text-xs text-text-hint mt-1">{t('billingPlans.noDuration')}</div>
                        )}
                        {selectedOption?.discount_percent ? (
                          <div className="text-xs text-emerald-600 mt-1">
                            {t('billingPlans.savePercent', { percent: selectedOption.discount_percent })}
                          </div>
                        ) : null}
                      </div>
                      <ul className="mt-3 space-y-1 text-xs text-text-muted">
                        {features.length > 0 ? (
                          features.map((feature) => <li key={feature}>{feature}</li>)
                        ) : (
                          <>
                            <li>{plan?.credits_included ?? 0} tín dụng/tháng</li>
                            <li>{plan?.basic_workflow_slots ?? 0} workflow cơ bản</li>
                            <li>{plan?.advanced_workflow_slots ?? 0} workflow nâng cao</li>
                          </>
                        )}
                      </ul>
                      <button
                        className="btn-primary w-full justify-center mt-3"
                        disabled={busy || !selectedOption}
                        onClick={() => handleSubscribe(plan.code, selectedMonths)}
                      >
                        {busy ? <Spinner /> : null}
                        <span>Đăng ký</span>
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* SECTION 3 — Topup Packages */}
        {!isInternal && (
          <div>
            <p className="text-primary font-bold text-sm mb-2">Nạp thêm tín dụng</p>

            {topupPackages === null ? (
              <div className="grid grid-cols-2 gap-2">
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {textPackages.length > 0 && (
                  <div>
                    <div className="text-text-hint text-xs uppercase tracking-wider mb-2">
                      Gói Text
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {textPackages.map(([key, pkg]) => {
                        const busy = topupKey === key
                        return (
                          <div key={key} className="card p-4">
                            <div className="text-primary font-bold text-sm">
                              {pkg?.label ?? key}
                            </div>
                            <div className="text-text-hint text-xs mt-1">
                              {pkg?.credits ?? 0} tín dụng
                            </div>
                            <div className="text-text-main font-semibold text-sm mt-2">
                              {formatVND(pkg?.price_vnd)}
                            </div>
                            <button
                              className="btn-primary w-full justify-center mt-3"
                              disabled={busy}
                              onClick={() => handleTopup(key)}
                            >
                              {busy ? <Spinner /> : null}
                              <span>Nạp ngay</span>
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {voicePackages.length > 0 && (
                  <div>
                    <div className="text-text-hint text-xs uppercase tracking-wider mb-2">
                      Gói Voice
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {voicePackages.map(([key, pkg]) => {
                        const busy = topupKey === key
                        return (
                          <div key={key} className="card p-4">
                            <div className="text-primary font-bold text-sm">
                              {pkg?.label ?? key}
                            </div>
                            <div className="text-text-hint text-xs mt-1">
                              {pkg?.credits ?? 0} tín dụng
                            </div>
                            <div className="text-text-main font-semibold text-sm mt-2">
                              {formatVND(pkg?.price_vnd)}
                            </div>
                            <button
                              className="btn-primary w-full justify-center mt-3"
                              disabled={busy}
                              onClick={() => handleTopup(key)}
                            >
                              {busy ? <Spinner /> : null}
                              <span>Nạp ngay</span>
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SECTION 4 — Transaction History */}
        <div>
          <p className="text-primary font-bold text-sm mb-2">Lịch sử & chu kỳ</p>
          <div className="card p-4">
            {status === null ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-48" />
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-40" />
              </div>
            ) : (
              <dl className="text-xs space-y-2">
                <div className="flex justify-between">
                  <dt className="text-text-hint">Hết hạn gói</dt>
                  <dd className="text-text-main font-semibold">
                    {status?.term_end
                      ? formatDate(status.term_end)
                      : status?.term_start ? 'Không thời hạn' : '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-hint">Kỳ tín dụng</dt>
                  <dd className="text-text-main font-semibold">
                    {formatDate(status?.billing_cycle_start)} → {formatDate(status?.billing_cycle_end)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-hint">Tín dụng đã dùng</dt>
                  <dd className="text-text-main font-semibold">
                    {status?.credits_used ?? 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-hint">Tín dụng đã mua</dt>
                  <dd className="text-text-main font-semibold">
                    {status?.credits_purchased ?? 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-hint">Tín dụng bonus</dt>
                  <dd className="text-text-main font-semibold">
                    {status?.credits_bonus ?? 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-hint">Chế độ vượt hạn</dt>
                  <dd className="text-text-main font-semibold">
                    {status?.overage_mode ?? '—'}
                  </dd>
                </div>
              </dl>
            )}
          </div>
        </div>

      </div>
    </ResponsiveShell>
  )
}
